import type { LightEntry, PageFields } from '@beat-venues/common-types';
import { LocaleUtil, urlUtil } from '@beat-venues/utils';
import {
    documentToReactComponents,
    Options,
} from '@contentful/rich-text-react-renderer';
import {
    type Block,
    type Document,
    type Inline,
    BLOCKS,
    INLINES,
    MARKS,
} from '@contentful/rich-text-types';
import { ReactNode } from 'react';

import type { CommonProps } from '../../types';
import { Button } from '../button';
import { Heading } from '../heading';
import { Paragraph } from '../paragraph';
import { TextLink } from '../text-link';
import { tv } from 'tailwind-variants';

export interface RichTextProps extends CommonProps, GetOptionsParams {
    body: Document;
    locale: string;
}

interface GetOptionsParams {
    paragraphSize?: 'lg' | 'md' | 'sm';
    disableMultiline?: boolean;
    disableLineBreaks?: boolean;
    disableHeadings?: boolean;
    disableLists?: boolean;
    disableHr?: boolean;
    disableTextLink?: boolean;
    headingSize?: 'sm';
    renderEmbeddedEntries?: (
        node: Block | Inline,
        children: ReactNode
    ) => JSX.Element;
}

const isRecord = (obj: unknown): obj is Record<string, unknown> =>
    typeof obj === 'object';

const isValidPageEntry = (node: unknown): node is LightEntry<PageFields> => {
    if (typeof node !== 'object') {
        return false;
    }

    if (!isRecord(node)) {
        return false;
    }

    return (
        Object.keys(node).includes('sys') &&
        Object.keys(node).includes('fields')
    );
};

const RichTextStyling = tv({
    base: 'flex flex-col gap-4',
});

const getOptions = (params: GetOptionsParams, locale: string): Options => {
    const HeadingStyles = tv({
        // All headings should get a margin top so each section is seperated. But whenever a heading has a heading as a
        // direct sibling, it should remove that margin
        base: '[&:not(:first-child)]:mt-8 [&_+_h2]:!mt-0 [&_+_h3]:!mt-0 [&_+_h4]:!mt-0 [&_+_h5]:!mt-0',
        variants: {
            size: {
                h2: 'mb-2',
                h3: 'mb-2',
                h4: 'mb-0',
                h5: 'mb-0',
                h6: 'mb-0',
            },
        },
    });
    return {
        renderMark: {
            [MARKS.BOLD]: (text: ReactNode) => (
                <b className="font-bold">{text}</b>
            ),
            [MARKS.UNDERLINE]: (text: ReactNode) => text,
        },
        renderNode: {
            [BLOCKS.PARAGRAPH]: (node, children) => {
                return (
                    <Paragraph className="mb-0" size={params.paragraphSize}>
                        {children}
                    </Paragraph>
                );
            },
            [BLOCKS.HEADING_2]: (node, children) =>
                !params.disableHeadings && (
                    <Heading
                        as="h2"
                        className={HeadingStyles({ size: 'h2' })}
                        {...(params.headingSize === 'sm'
                            ? { size: 'h3' }
                            : undefined)}
                    >
                        {children}
                    </Heading>
                ),
            [BLOCKS.HEADING_3]: (node, children) =>
                !params.disableHeadings && (
                    <Heading
                        as="h3"
                        className={HeadingStyles({ size: 'h3' })}
                        {...(params.headingSize === 'sm'
                            ? { size: 'h4' }
                            : undefined)}
                    >
                        {children}
                    </Heading>
                ),
            [BLOCKS.HEADING_4]: (node, children) =>
                !params.disableHeadings && (
                    <Heading as="h4" className={HeadingStyles({ size: 'h4' })}>
                        {children}
                    </Heading>
                ),
            [BLOCKS.HEADING_5]: (node, children) =>
                !params.disableHeadings && (
                    <Heading as="h5" className={HeadingStyles({ size: 'h5' })}>
                        {children}
                    </Heading>
                ),
            [BLOCKS.HEADING_6]: (node, children) =>
                !params.disableHeadings && (
                    <Heading as="h6" className={HeadingStyles({ size: 'h6' })}>
                        {children}
                    </Heading>
                ),
            [BLOCKS.HR]: () => !params.disableHr && <hr className="mb-4" />,
            [BLOCKS.UL_LIST]: (node, children) =>
                !params.disableLists && (
                    <ul className="list-disc pl-8">{children}</ul>
                ),
            [BLOCKS.OL_LIST]: (node, children) =>
                !params.disableLists && (
                    <ol className="list-decimal pl-8">{children}</ol>
                ),
            [BLOCKS.LIST_ITEM]: (node) => {
                const defaultOptions = getOptions(params, locale);
                return documentToReactComponents(node as Document, {
                    ...defaultOptions,
                    renderNode: {
                        ...defaultOptions.renderNode,
                        [BLOCKS.LIST_ITEM]: (node, children) => (
                            <li className="mb-4 text-base font-light leading-6">
                                {children}
                            </li>
                        ),
                    },
                });
            },
            [BLOCKS.TABLE]: (node, children) => (
                <div className="mb-4 overflow-auto">
                    <table className="min-w-full">{children}</table>
                </div>
            ),
            [BLOCKS.TABLE_ROW]: (node, children) => {
                if (
                    children &&
                    Array.isArray(children) &&
                    children.every(
                        (node: Block | Inline) =>
                            node.nodeType === BLOCKS.TABLE_HEADER_CELL
                    )
                ) {
                    // all children are header cells, so we should wrap the row
                    // with a <thead /> tag
                    return (
                        <thead>
                            <tr className="group">{children}</tr>
                        </thead>
                    );
                } else {
                    // not a header row, so we render a tbody with tr
                    return (
                        <tbody className="group">
                            <tr>{children}</tr>
                        </tbody>
                    );
                }
            },
            [BLOCKS.TABLE_HEADER_CELL]: (node) => {
                const defaultOptions = getOptions(
                    { ...params, disableLineBreaks: true },
                    locale
                );
                return documentToReactComponents(node as Document, {
                    ...defaultOptions,

                    renderNode: {
                        ...defaultOptions.renderNode,
                        [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
                            <th className="text-bold whitespace-nowrap border-b border-white p-2 text-left first:pl-4 last:pr-4">
                                {children}
                            </th>
                        ),
                    },
                });
            },
            [BLOCKS.TABLE_CELL]: (node) => {
                const defaultOptions = getOptions(
                    { ...params, disableLineBreaks: true },
                    locale
                );
                return documentToReactComponents(node as Document, {
                    ...defaultOptions,

                    renderNode: {
                        ...defaultOptions.renderNode,
                        [BLOCKS.TABLE_CELL]: (node, children) => (
                            <td className="min-w-40 p-2 align-top first:pl-4 last:pr-4 group-odd:bg-gray-500">
                                {children}
                            </td>
                        ),
                    },
                });
            },
            [INLINES.ENTRY_HYPERLINK]: (node, children) => {
                const entry = node.data['target'];
                if (!isValidPageEntry(entry)) {
                    return children;
                }

                const linkProps = urlUtil.resolveLink(
                    entry,
                    LocaleUtil.isValidLocaleOrDefault(entry.sys.locale),
                    locale
                );
                if (!linkProps) {
                    return children;
                }

                if (params.disableTextLink) {
                    return <Button {...linkProps}>{children}</Button>;
                }

                return (
                    <TextLink
                        className={params.disableMultiline ? 'text-black' : ''}
                        size="none"
                        weight="inherit"
                        {...linkProps}
                    >
                        {children}
                    </TextLink>
                );
            },
            [INLINES.HYPERLINK]: (node, children) => {
                if (params.disableTextLink) {
                    return <Button href={node.data['uri']}>{children}</Button>;
                }
                return (
                    <TextLink
                        className={params.disableMultiline ? 'text-black' : ''}
                        href={node.data['uri']}
                        size="none"
                        weight="inherit"
                    >
                        {children}
                    </TextLink>
                );
            },
            [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
                if (!params.renderEmbeddedEntries) {
                    return;
                }
                return params.renderEmbeddedEntries(node, children);
            },
        },
        renderText: (text: string) => {
            if (params.disableMultiline || params.disableLineBreaks) {
                return text.split('\n');
            }
            return text
                .split('\n')
                .reduce((children: ReactNode[], textSegment, index) => {
                    return [
                        ...children,
                        index > 0 && <br key={index} />,
                        textSegment,
                    ];
                }, []);
        },
    };
};

export const RichText = ({
    className,
    body,
    paragraphSize,
    disableMultiline,
    disableHeadings,
    disableLists,
    disableHr,
    disableTextLink,
    testId = 'rich-text',
    renderEmbeddedEntries,
    headingSize,
    locale,
    ...props
}: RichTextProps): JSX.Element => {
    return (
        <div
            className={RichTextStyling({ className })}
            data-testid={testId}
            {...props}
        >
            {documentToReactComponents(
                body,
                getOptions(
                    {
                        paragraphSize,
                        disableMultiline,
                        disableHeadings,
                        disableLists,
                        disableHr,
                        disableTextLink,
                        headingSize,
                        renderEmbeddedEntries,
                    },
                    locale
                )
            )}
        </div>
    );
};
